import { Component, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

export interface FaqCategory {
  id: string;
  title: string;
  items: FaqItem[];
}

const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: 'why-galopa',
    title: 'Why Galopa',
    items: [
      {
        id: 'how-different',
        question: 'How is Galopa different from other marketplaces?',
        answer:
          'Facebook Marketplace is built for face-to-face, local deals — you’re limited to whoever happens to be selling nearby. Galopa lets you buy and sell with people anywhere in the country, which completely opens up your market: sellers reach every buyer after their exact saddle instead of just the neighbourhood, and buyers can find the right brand, discipline, and tree width instead of settling for whatever’s listed close by. Every user is identity-verified, and payment isn’t handed over on trust — your funds are held securely by Galopa and only released to the seller once the saddle is delivered and you’ve confirmed it matches the listing (or the 48-hour inspection window passes with no issue reported).',
      },
    ],
  },
  {
    id: 'secure-trade',
    title: 'Secure Trade',
    items: [
      {
        id: 'what-is-secure-trade',
        question: 'Can I use Galopa to pay securely for a saddle I found on another marketplace?',
        answer:
          'Yes — that’s Secure Trade. Found a saddle on Facebook Marketplace, a noticeboard, or through word of mouth? Either the buyer or the seller can start a trade on Galopa for it, with the agreed saddle, price, and any conditions. Both parties need a free Galopa account — whoever starts the request invites the other by email, and they set one up to accept. Once accepted, the buyer pays in and Galopa holds the funds securely — not the seller — until the saddle is delivered and confirmed to match what was agreed, the same protection as buying a listing directly on Galopa.',
      },
    ],
  },
  {
    id: 'buying',
    title: 'Buying & Inspection',
    items: [
      {
        id: 'buyer-seller-protection',
        question: 'How does Galopa keep the sale low-risk for both buyer and seller?',
        answer:
          'The buyer pays upfront, but that payment isn’t handed to the seller directly — it’s captured by Galopa and held securely, so the seller knows the sale is real before they ship. Once the seller confirms the saddle’s been handed over — shipped and received, or picked up in person — and the buyer confirms it arrived, a 48-hour inspection window opens. Funds are only released to the seller once the buyer confirms it matches the listing, or the window closes with no issue reported. That means the seller is guaranteed payment for a completed sale, and the buyer’s money is never released until they’ve had the chance to check the saddle in hand.',
      },
      {
        id: 'when-inspection-starts',
        question: 'When exactly does the inspection window start?',
        answer:
          'Not the moment your offer is accepted. Your order first moves to “accepted” while the sale is arranged; the inspection window only starts once the seller has confirmed the saddle was shipped or handed over and you’ve confirmed you received it — whichever of those two confirmations happens second.',
      },
      {
        id: 'what-if-no-action',
        question: 'What happens if I don’t do anything during the 48 hours?',
        answer:
          'If you haven’t reported an issue, the sale automatically finalises at the end of the 48-hour window — the seller is paid out and we’ll email you to confirm the saddle is officially yours.',
      },
      {
        id: 'what-if-not-fit',
        question: 'What if the saddle doesn’t fit my horse?',
        answer:
          'The inspection window is for confirming the saddle matches how it was listed, not a general try-it-and-decide period. If it’s exactly as described but simply doesn’t suit your horse, it’s yours to keep — and you’re free to resell it on Galopa any time, for free, at 0% commission. If something’s genuinely wrong — damaged, not authentic, materially different from the listing — report it within the 48-hour window for a refund instead.',
      },
      {
        id: 'buyer-photos',
        question: 'Do I need to take photos when it arrives?',
        answer:
          'Yes. Take timestamped photos immediately upon unboxing. If there’s a dispute later, this baseline evidence is required — without it, you automatically forfeit your right to win a dispute.',
      },
      {
        id: 'contact-seller',
        question: 'Can I message the seller before buying?',
        answer:
          'Yes — chat on Galopa to ask about fit, history, and condition. Until a formal offer is accepted, don’t share personal contact details (email, phone, address, or off-platform links); messages are automatically screened for these until then.',
      },
    ],
  },
  {
    id: 'wanted-saddle',
    title: 'Saddle Finder',
    items: [
      {
        id: 'what-is-wanted-saddle',
        question: 'What is Saddle Finder?',
        answer:
          'It flips the usual search: instead of browsing listings, you post what saddle you’re after — brand, discipline, size, tree width, budget — and sellers with a matching saddle already listed can respond directly.',
      },
      {
        id: 'how-to-post-wanted',
        question: 'How do I post a wanted saddle?',
        answer:
          'From your account, go to My Wanted Saddles and add a new one. You don’t need photos — just describe what you’re looking for and publish it for sellers to see.',
      },
      {
        id: 'wanted-response',
        question: 'What happens when a seller responds?',
        answer:
          'They propose one of their own active listings and send you a message, which opens a normal conversation in your Messages. If it’s a match, buying goes through that listing’s usual Buy Now or Make an Offer flow — posting a wanted saddle never charges your card directly.',
      },
      {
        id: 'wanted-moderation',
        question: 'Is a wanted saddle moderated the same way as messages?',
        answer:
          'Yes — its title and description are screened for phone numbers, emails, and other off-platform contact details, the same as pre-offer messages.',
      },
    ],
  },
  {
    id: 'selling',
    title: 'Selling',
    items: [
      {
        id: 'how-to-list',
        question: 'How do I list a saddle?',
        answer:
          'Create an account, then add your listing with accurate photos and a full description (brand, model, discipline, seat size, gullet, flocking, age, and every flaw). Listings that don’t meet our publication rules may be unpublished.',
      },
      {
        id: 'photo-requirements',
        question: 'What photos do I need?',
        answer:
          'Include clear, well-lit shots of both side profiles, front (pommel & gullet), rear (cantle & channel), underneath panels, girth points/billets, under-flap stamps, and close-ups of any damage. Also keep timestamped pre-boxing photos on your phone for dispute protection.',
      },
      {
        id: 'when-paid',
        question: 'When do I get paid?',
        answer:
          'Once the sale completes — either the buyer confirms it’s as described, or their 48-hour inspection window ends with no issue reported — you’re paid the sale price minus your selling commission. If the buyer chose delivery, the shipping insurance and delivery fee they paid are passed through to you as well, to cover your postage.',
      },
      {
        id: 'extra-cover',
        question: 'Do I need to buy Extra Cover when I post a saddle?',
        answer:
          'Yes, for delivery orders — add Australia Post’s Extra Cover for the full sale value and Signature on Delivery when you lodge the parcel. The buyer has already paid for both as part of your payout, but Australia Post won’t add them automatically — you need to select them yourself at lodgement.',
      },
      {
        id: 'listing-fees',
        question: 'Are there listing fees, and how much is the commission?',
        answer:
          'There’s no fee to list. Galopa’s standard selling commission is 8%, deducted from your payout when a sale completes. Some sellers get a reduced rate — typically 3-5% — through a promo code or an arrangement negotiated directly with Galopa; check your account to see your current rate.',
      },
    ],
  },
  {
    id: 'shipping',
    title: 'Shipping & Delivery',
    items: [
      {
        id: 'who-pays-shipping',
        question: 'Who pays for shipping?',
        answer:
          'If a buyer chooses delivery, they pay a 1% shipping-insurance fee plus a delivery fee — calculated live through Australia Post at checkout — on top of the item price. Both are passed through to the seller at payout to cover their actual postage cost. Choosing local pickup instead means neither fee applies. If an item is returned, the buyer arranges and pays for return shipping themselves.',
      },
      {
        id: 'how-long-delivery',
        question: 'How long does delivery take?',
        answer:
          'Timing depends on the seller’s location and the carrier they choose. Once a formal offer is accepted, shipping details are shared so the seller can dispatch the saddle promptly.',
      },
      {
        id: 'track-order',
        question: 'Can I track my order?',
        answer:
          'Yes — once the seller ships, tracking details are shared through the order flow so both parties can follow delivery and returns.',
      },
    ],
  },
  {
    id: 'returns',
    title: 'Refunds & Disputes',
    items: [
      {
        id: 'how-to-report-issue',
        question: 'How do I report an issue with a saddle I bought?',
        answer:
          'If it arrives not as described — damaged, inauthentic, or materially different from the listing — report it from your order within the 48-hour inspection window, describing the issue. Once the seller reviews and agrees, pack the saddle carefully and send it back at your own cost (or return it in person for a local-pickup order); once they confirm it arrived back in the same condition it was sent, your refund is processed. If the saddle is simply not right for your horse but otherwise as described, keep it — you can resell it on Galopa any time, for free, at 0% commission.',
      },
      {
        id: 'what-refunded',
        question: 'What am I refunded if my report is approved?',
        answer:
          'Everything you were charged: the item price, the buyer service fee, and — if you chose delivery — the shipping insurance and delivery fee. The only cost you don’t get back is what you pay to ship the saddle back.',
      },
      {
        id: 'damaged-return',
        question: 'What if the saddle is damaged during the inspection window?',
        answer:
          'If evidence shows the buyer damaged the saddle, funds are released to the seller and the buyer keeps the saddle (forced purchase). Always photograph on arrival and treat the saddle carefully while inspecting it.',
      },
    ],
  },
  {
    id: 'payments',
    title: 'Payments & Fees',
    items: [
      {
        id: 'holding-funds',
        question: 'How are my funds held during the inspection window?',
        answer:
          'When your offer or buy-now request is accepted, your card is charged immediately via Stripe and the funds are held securely by Galopa. They’re only released once the inspection window resolves — you confirm it’s as described, the 48 hours pass with no issue reported, or your refund is processed.',
      },
      {
        id: 'buyer-fee',
        question: 'What is the buyer service fee?',
        answer:
          'A fixed 3% is added to every order at checkout, regardless of delivery method — it’s never discounted. Choosing delivery adds a further 1% shipping-insurance fee plus a live Australia Post delivery quote; local pickup has neither.',
      },
      {
        id: 'payment-methods',
        question: 'What payment methods are accepted?',
        answer:
          'Payments and identity verification (KYC) are handled securely by Stripe. Galopa does not store your full financial details or government ID on our servers.',
      },
    ],
  },
  {
    id: 'account',
    title: 'Account & Safety',
    items: [
      {
        id: 'kyc',
        question: 'Why do I need identity verification?',
        answer:
          'KYC helps keep the marketplace safe and is processed by Stripe. Accounts that fail verification or engage in fraudulent activity may be suspended.',
      },
      {
        id: 'no-pii',
        question: 'Why can’t I share my phone number or email?',
        answer:
          'To prevent off-platform deals and protect both sides, PII (email, phone, address, external links) can’t be shared until a formal offer is accepted. Violations can result in immediate suspension.',
      },
      {
        id: 'disputes',
        question: 'How are disputes handled?',
        answer:
          'Galopa reviews unboxing and re-boxing evidence and acts as final arbitrator. Our decision on releasing held funds is binding. See the Terms of Use for scenario outcomes.',
      },
      {
        id: 'more-legal',
        question: 'Where can I read the full legal terms?',
        answer:
          'See our Terms of Use (including seller publication rules) and Privacy Policy, linked in the site footer.',
      },
    ],
  },
];

@Component({
  selector: 'app-help',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './help.component.html',
})
export class HelpComponent {
  readonly query = signal('');
  readonly openId = signal<string | null>(null);

  readonly categories = computed(() => {
    const q = this.query().trim().toLowerCase();
    if (!q) return FAQ_CATEGORIES;

    return FAQ_CATEGORIES.map((category) => ({
      ...category,
      items: category.items.filter(
        (item) =>
          item.question.toLowerCase().includes(q) ||
          item.answer.toLowerCase().includes(q) ||
          category.title.toLowerCase().includes(q),
      ),
    })).filter((category) => category.items.length > 0);
  });

  toggle(id: string): void {
    this.openId.update((current) => (current === id ? null : id));
  }

  onSearchSubmit(event: Event): void {
    event.preventDefault();
    const first = this.categories()[0]?.items[0];
    if (first) this.openId.set(first.id);
  }
}
